/* eslint-disable */
import type { ChangeEvent } from 'react';
import React from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import type { SearchFieldProps } from '@oneaudi/unified-web-components';
import { SearchField } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import { messages } from '../../../i18n/messages';
import { useHandleClearInput, useHandleInputChange } from './SearchInput.handlers';
import mapContext from '../../../contexts/map-context';
import searchGroupContext from '../../../contexts/search-group-context';
import config from '../../../config/config.json';

const { searchInputId } = config;

const SearchStyled = styled(SearchField)<
  SearchFieldProps & {
    'aria-label': string;
    role: string;
    'aria-autocomplete': string;
    'aria-expanded': boolean;
    'aria-controls': string;
  }
>`
  input::-webkit-search-cancel-button {
    display: none;
  }
  min-width: unset;
`;

export const SearchInput = () => {
  const { searchText, isAutocompletionOpen, showPartnerSuggestions } = mapContext.useMapState();
  const [enterBtnPressed, setEnterBtnPressed] = React.useState(false);
  const handleOnChange = useHandleInputChange(enterBtnPressed, setEnterBtnPressed);
  const fieldHint = showPartnerSuggestions
    ? useI18n(messages.search_box_hint)
    : useI18n(messages.audi_partners_off_search_box_hint);
  const placeholder = useI18n(messages.search_box_placeholder);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const handleClean = useHandleClearInput(true, inputRef);

  const { onToggle } = searchGroupContext.useToggle();

  const handleOnFocus = () => {
    onToggle(true);
  };

  React.useEffect(() => {
    const actualInput = inputRef.current?.querySelector('input[type="search"]');
    if (actualInput) {
      actualInput.setAttribute('autocomplete', 'off');
    }
  }, []);

  // Force autocomplete attribute on the internal input element
  React.useEffect(() => {
    if (inputRef.current) {
      const realInput = inputRef.current.querySelector('input');
      if (realInput) {
        realInput.setAttribute('autocomplete', 'off');
      }
    }
  }, []);

  return (
    <SearchStyled
      className="search-dealer-field"
      id={searchInputId}
      aria-label="Search Dealer Input"
      role="combobox"
      aria-autocomplete="list"
      aria-expanded={isAutocompletionOpen}
      aria-controls={`${searchInputId}-options`}
      hint={fieldHint}
      label={placeholder}
      onChange={handleOnChange as (event: ChangeEvent<HTMLInputElement>) => void}
      value={searchText}
      data-test-id="search-input-container"
      ref={inputRef}
      clearButton={{
        'aria-label': 'Clear Button',
        onClick: handleClean,
      }}
      onClick={handleOnFocus}
    />
  );
};
